import { useState } from "react";
import { KeyRound, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { ErrorBox, Field, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";
import { CredentialDialog } from "@/components/CredentialDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ROLE_LABEL = { super_admin: "Super Admin", admin: "Admin", cashier: "Cashier", provider: "Provider" };

function StaffForm({ isSuper, onIssued }) {
  const providers = useFetch("/admin/providers");
  const [f, setF] = useState({ username: "", full_name: "", role: "provider", provider_id: "" });
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/admin/staff", { ...f, provider_id: f.provider_id || null }); onIssued({ username: data.user.username, temp_password: data.temp_password }); }
    catch (err) { setError(errMsg(err)); }
  };
  const roles = isSuper ? ["provider", "admin", "super_admin"] : ["provider"];
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="staff-form">
      <Field label="Username"><input className="field" value={f.username} onChange={set("username")} required data-testid="staff-username-input" /></Field>
      <Field label="Full name"><input className="field" value={f.full_name} onChange={set("full_name")} required data-testid="staff-name-input" /></Field>
      <Field label="Role">
        <select className="field" value={f.role} onChange={set("role")} data-testid="staff-role-select">{roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}</select>
      </Field>
      {f.role === "provider" && (
        <Field label="Linked service provider">
          <select className="field" value={f.provider_id} onChange={set("provider_id")} required data-testid="staff-provider-select">
            <option value="">Select…</option>{providers.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      )}
      <ErrorBox error={error} />
      <button className="btn-primary w-full" data-testid="staff-create-submit">Create & generate temporary password</button>
    </form>
  );
}

export default function Staff() {
  const { user } = useAuth();
  const { data, loading, reload } = useFetch("/admin/staff");
  const [open, setOpen] = useState(false);
  const [creds, setCreds] = useState(null);
  const isSuper = user.role === "super_admin";
  const reset = async (u) => {
    if (!window.confirm(`Reset password for ${u.username}?`)) return;
    try { const { data: d } = await api.post(`/admin/staff/${u.id}/reset-password`); setCreds(d); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (u) => {
    try { await api.patch(`/admin/users/${u.id}/active`, { active: !u.active }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (loading && !data) return <Spinner />;
  return (
    <div>
      <PageHead eyebrow="Staff accounts" title="Staff & roles" sub="Super Admin, Admin, Cashier and Doctor / Provider accounts with server-side role controls."
        actions={<button className="btn-primary" onClick={() => setOpen(true)} data-testid="add-staff-button"><UserPlus size={15} /> Add staff</button>} />
      <div className="surface overflow-x-auto">
        <table className="data-table" data-testid="staff-table">
          <thead><tr><th>Username</th><th>Name</th><th>Role</th><th>Last login</th><th>Status</th><th /></tr></thead>
          <tbody>{data?.map((u) => (
            <tr key={u.id} data-testid={`staff-row-${u.username}`}>
              <td className="font-mono text-xs font-semibold">{u.username}</td><td>{u.full_name}</td><td>{ROLE_LABEL[u.role]}</td>
              <td className="text-xs">{fmtDateTime(u.last_login_at)}</td><td><StatusBadge status={u.active ? "ACTIVE" : "INACTIVE"} /></td>
              <td className="whitespace-nowrap text-right">
                {u.id !== user.id && (isSuper || ["cashier", "provider"].includes(u.role)) && (
                  <>
                    <button className="btn-ghost btn-sm" onClick={() => reset(u)} data-testid={`staff-reset-${u.username}`}><KeyRound size={13} /> Reset</button>
                    <button className="btn-ghost btn-sm" onClick={() => toggle(u)} data-testid={`staff-toggle-${u.username}`}>{u.active ? "Disable" : "Enable"}</button>
                  </>
                )}
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-3xl"><DialogHeader><DialogTitle>Add staff account</DialogTitle></DialogHeader>
          <StaffForm isSuper={isSuper} onIssued={(c) => { setOpen(false); setCreds(c); reload(); }} />
        </DialogContent>
      </Dialog>
      <CredentialDialog creds={creds} onClose={() => setCreds(null)} />
    </div>
  );
}
